import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSitemap } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

interface DeptRow { department: string; department_label: string; default_dashboards: { id: string; label: string }[]; user_count: number; }
interface RoleRow { role: string; role_label: string; user_count: number; }
interface DashRow { id: string; label: string; active_grant_count: number; }

const RolePermissionMatrix: React.FC = () => {
    const { api } = useInternalApi();
    const [departments, setDepartments] = useState<DeptRow[]>([]);
    const [roles, setRoles] = useState<RoleRow[]>([]);
    const [dashboards, setDashboards] = useState<DashRow[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Role & Permission Matrix - Uzima Internal'; }, []);

    useEffect(() => {
        api.get('platform/admin/role-permission-matrix/').then(res => {
            if (res.data?.status === 'success') {
                setDepartments(res.data.data.departments || []);
                setRoles(res.data.data.roles || []);
                setDashboards(res.data.data.internal_dashboards || []);
            }
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faSitemap} className="me-2 text-primary" />Role & Permission Matrix</h2>
            <p className="text-muted mb-4">Which department unlocks which internal dashboards by default, and current headcount</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white"><strong>Departments → Default Dashboards</strong></div>
                        <div className="card-body p-0">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light"><tr><th>Department</th><th>Users</th><th>Default Dashboards</th></tr></thead>
                                <tbody>
                                    {departments.map(d => (
                                        <tr key={d.department}>
                                            <td><strong>{d.department_label}</strong></td>
                                            <td><span className="badge bg-primary">{d.user_count}</span></td>
                                            <td>
                                                {d.default_dashboards.length === 0 ? <span className="text-muted">None</span> :
                                                    d.default_dashboards.map(db => <span key={db.id} className="badge bg-light text-dark me-1">{db.label}</span>)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white"><strong>Roles</strong></div>
                                <div className="card-body p-0">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light"><tr><th>Role</th><th>Users</th></tr></thead>
                                        <tbody>
                                            {roles.map(r => (
                                                <tr key={r.role}><td>{r.role_label}</td><td><span className="badge bg-primary">{r.user_count}</span></td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white"><strong>Internal Dashboards — Active Grants</strong></div>
                                <div className="card-body p-0">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light"><tr><th>Dashboard</th><th>Active Grants</th></tr></thead>
                                        <tbody>
                                            {dashboards.map(d => (
                                                <tr key={d.id}><td>{d.label}</td><td><span className="badge bg-info">{d.active_grant_count}</span></td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default RolePermissionMatrix;
